export const ONBOARDING_VERSION = 2;

export const CREDIT_PRODUCTS = [
  {
    id: "bnpl",
    name: "Buy now, pay later",
    shortName: "BNPL",
    description: "Consumer or B2B purchases repaid in scheduled installments.",
  },
  {
    id: "mca",
    name: "Merchant cash advance",
    shortName: "MCA",
    description: "An advance repaid from a merchant's future sales.",
  },
  {
    id: "net_terms",
    name: "Invoice / trade credit",
    shortName: "Net terms",
    description: "B2B invoices offered on Net 15, 30, 60, 90, or custom terms.",
  },
  {
    id: "credit_card",
    name: "Credit card or revolving line",
    shortName: "Revolving",
    description: "Reusable credit with statements, minimum payments, or interest-free days.",
  },
  {
    id: "factoring",
    name: "Invoice factoring",
    shortName: "Factoring",
    description: "Receivables financed against invoices, with or without recourse.",
  },
  {
    id: "leasing",
    name: "Equipment finance / leasing",
    shortName: "Leasing",
    description: "Asset-backed financing repaid over a fixed lease term.",
  },
  {
    id: "other",
    name: "Another credit model",
    shortName: "Other",
    description: "A custom, hybrid, or specialist credit product.",
  },
];

const field = (key, label, options = {}) => ({ key, label, ...options });

export const PRODUCT_TERM_FIELDS = {
  bnpl: [
    field("customerType", "Who receives the credit?", { type: "select", options: ["Consumers (B2C)", "Businesses (B2B)", "Both"] }),
    field("averageTicket", "Average financed purchase", { prefix: "$", type: "number", placeholder: "250" }),
    field("averageTermDays", "Average credit term", { suffix: "days", type: "number", placeholder: "45" }),
    field("installments", "Typical number of installments", { type: "number", placeholder: "4" }),
    field("paymentFrequency", "Payment frequency", { type: "select", options: ["Weekly", "Biweekly", "Monthly", "Custom"] }),
    field("downPaymentPercent", "Typical down payment", { suffix: "%", type: "number", placeholder: "25", optional: true }),
  ],
  mca: [
    field("averageAdvance", "Average advance amount", { prefix: "$", type: "number", placeholder: "50,000" }),
    field("averageReturnDays", "Average expected return time", { suffix: "days", type: "number", placeholder: "180" }),
    field("factorRate", "Typical factor rate", { type: "number", placeholder: "1.30" }),
    field("remittanceFrequency", "Remittance frequency", { type: "select", options: ["Daily", "Weekly", "Biweekly", "Monthly"] }),
    field("holdbackPercent", "Typical share of sales withheld", { suffix: "%", type: "number", placeholder: "12", optional: true }),
    field("secured", "Is the advance secured?", { type: "select", options: ["Unsecured", "Secured", "Both"] }),
  ],
  net_terms: [
    field("customerType", "Who receives the credit?", { type: "select", options: ["Businesses (B2B)", "Consumers (B2C)", "Both"] }),
    field("averageInvoice", "Average invoice amount", { prefix: "$", type: "number", placeholder: "10,000" }),
    field("standardTermDays", "Most common payment term", { type: "select", options: ["Net 15", "Net 30", "Net 45", "Net 60", "Net 90", "Custom"] }),
    field("averageActualPaymentDays", "Average actual payment time", { suffix: "days", type: "number", placeholder: "38" }),
    field("gracePeriodDays", "Grace period before delinquency", { suffix: "days", type: "number", placeholder: "5", optional: true }),
    field("averageCreditLimit", "Average customer credit limit", { prefix: "$", type: "number", placeholder: "25,000", optional: true }),
  ],
  credit_card: [
    field("customerType", "Who receives the credit?", { type: "select", options: ["Consumers (B2C)", "Businesses (B2B)", "Both"] }),
    field("averageCreditLimit", "Average credit limit", { prefix: "$", type: "number", placeholder: "5,000" }),
    field("averageCreditTermDays", "Average interest-free credit term", { suffix: "days", type: "number", placeholder: "30" }),
    field("statementCycleDays", "Statement cycle", { suffix: "days", type: "number", placeholder: "30" }),
    field("minimumPaymentPercent", "Typical minimum payment", { suffix: "%", type: "number", placeholder: "5" }),
    field("revolvingBalancePercent", "Customers carrying a balance", { suffix: "%", type: "number", placeholder: "40", optional: true }),
  ],
  factoring: [
    field("averageInvoice", "Average invoice amount", { prefix: "$", type: "number", placeholder: "20,000" }),
    field("averageMaturityDays", "Average invoice maturity", { suffix: "days", type: "number", placeholder: "60" }),
    field("advanceRatePercent", "Typical advance rate", { suffix: "%", type: "number", placeholder: "85" }),
    field("recourse", "Factoring structure", { type: "select", options: ["With recourse", "Without recourse", "Both"] }),
    field("feeStructure", "Typical fee structure", { type: "select", options: ["Flat fee", "Fee per period", "Interest rate", "Custom"] }),
    field("concentrationLimitPercent", "Single-debtor concentration limit", { suffix: "%", type: "number", placeholder: "20", optional: true }),
  ],
  leasing: [
    field("averageFinancedAmount", "Average financed amount", { prefix: "$", type: "number", placeholder: "75,000" }),
    field("averageTermMonths", "Average lease term", { suffix: "months", type: "number", placeholder: "36" }),
    field("paymentFrequency", "Payment frequency", { type: "select", options: ["Monthly", "Quarterly", "Seasonal", "Custom"] }),
    field("downPaymentPercent", "Typical down payment", { suffix: "%", type: "number", placeholder: "10", optional: true }),
    field("residualValuePercent", "Typical residual value", { suffix: "%", type: "number", placeholder: "15", optional: true }),
    field("assetType", "Main asset type", { placeholder: "Vehicles, machinery, technology…" }),
  ],
  other: [
    field("productName", "Name of the credit product", { placeholder: "Describe the model in a few words" }),
    field("customerType", "Who receives the credit?", { type: "select", options: ["Consumers (B2C)", "Businesses (B2B)", "Both"] }),
    field("averageExposure", "Average exposure per customer", { prefix: "$", type: "number", placeholder: "25,000" }),
    field("averageTerm", "Typical time to full repayment", { placeholder: "For example: 90 days or 24 months" }),
    field("repaymentMethod", "How is it repaid?", { placeholder: "For example: monthly or percentage of sales" }),
  ],
};

export const getCreditProduct = (id) => CREDIT_PRODUCTS.find((product) => product.id === id);
